import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetRolesListQuery, useDeleteRoleMutation, useUpdateRoleMutation } from "../../store/api/manageAPI";
import Pagination from "../../components/Panigation";
import moment from "moment";
import { showToast, showError } from "../../constants/toasts";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";

const RolesList = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [searchTerm] = useState("");

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);

    const { data: rolesData, isLoading, isFetching } = useGetRolesListQuery({
        page: page,
        limit: rowsPerPage,
        search: searchTerm,
    });

    const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();
    const [updateRole] = useUpdateRoleMutation();

    const rows = rolesData?.data?.list || [];
    const totalCount = rolesData?.data?.totalCount || 0;

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleDeleteClick = (id: string) => {
        setSelectedDeleteId(id);
        setDeleteModalOpen(true);
    };

    const handleDeleteClose = () => {
        setDeleteModalOpen(false);
        setSelectedDeleteId(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedDeleteId) return;

        try {
            const response = await deleteRole({ id: selectedDeleteId }).unwrap();
            if (response && (response.success || response.message)) {
                showToast(response.message || "Role deleted successfully");
                handleDeleteClose();
            }
        } catch (error: any) {
            showError(error?.data?.message || "Failed to delete role");
        }
    };

    const handleStatusChange = async (row: any) => {
        try {
            const response = await updateRole({
                id: row._id,
                name: row.name,
                isBlocked: !row.isBlocked,
                permissions: row.permissions
            }).unwrap();

            if (response && (response.success || response.message)) {
                showToast(response.message || "Status updated successfully");
            }
        } catch (error: any) {
            showError(error?.data?.message || "Failed to update status");
        }
    };


    return (
        <>
            <TableContainer className="table_container">
                <Table>
                    <TableHead>
                        <TableRow hover>
                            <TableCell>Role Name</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading || isFetching ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" style={{ padding: "50px" }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : rows.length > 0 ? (
                            rows.map((row: any) => (
                                <TableRow hover key={row._id}>
                                    <TableCell>
                                        <a
                                            href={`/manage-subadmin/roles/detail/${row._id}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`/manage-subadmin/roles/detail/${row._id}`);
                                            }}
                                            className="user_block click_action"
                                        >
                                            <p>
                                                <b>{row.name}</b>
                                            </p>
                                        </a>

                                    </TableCell>
                                    <TableCell>{moment(row.createdAt).format("DD MMMM YYYY")}</TableCell>
                                    <TableCell>
                                        <SwitchToggle
                                            checked={!row.isBlocked}
                                            onChange={() => handleStatusChange(row)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="table_actions v2">
                                            <Box>
                                                <IconButton
                                                    onClick={() => navigate(`/manage-subadmin/roles/detail/${row._id}`)}
                                                >
                                                    <img src="/static/images/eye_icon.svg" alt="view" />
                                                </IconButton>
                                            </Box>
                                            <Box>
                                                <IconButton
                                                    onClick={() => navigate(`/manage-subadmin/roles/edit/${row._id}`)}
                                                >
                                                    <img src="/static/images/edit2_icon.svg" alt="edit" />
                                                </IconButton>
                                            </Box>
                                            <Box>
                                                <IconButton onClick={() => handleDeleteClick(row._id)}>
                                                    <img src="/static/images/trash2_icon.svg" alt="delete" />
                                                </IconButton>
                                            </Box>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No records found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {!isLoading && totalCount > 0 && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Pagination
                        module={null}
                        page={page}
                        setPage={setPage as any}
                        onPageChange={handlePageChange}
                        totalPages={Math.ceil(totalCount / rowsPerPage)}
                        totalCount={totalCount}
                        limit={rowsPerPage}
                    />
                </Box>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                onClose={handleDeleteClose}
                onConfirm={handleConfirmDelete}
                itemName={rows.find((r: any) => r._id === selectedDeleteId)?.name || "Role"}
                isLoading={isDeleting}
            />
        </>
    );
};

export default RolesList;
